/**
 * Release-features bootstrap client + in-memory store.
 *
 * Fetches the PUBLIC `GET /api/v1/release-features` endpoint ONCE before first
 * meaningful paint and caches the resolved set of deployment-wide enabled
 * release-toggle keys. The frontend is a PURE CONSUMER of this server-resolved
 * set — it performs NO client-side flag re-evaluation
 * (SPEC-FEATURE-TOGGLES-RULE-002).
 *
 * FAIL CLOSED (SPEC-FEATURE-TOGGLES-RULE-006, SCENARIO-004): any non-200,
 * network error, timeout, or empty/malformed body yields an EMPTY enabled set
 * — every gated feature is hidden. There is NO default-enabled-on-error path.
 *
 * Source endpoint contract (SPEC-FEATURE-TOGGLES-CONTRACT-001):
 *   GET /api/v1/release-features (public/skipPaths, principal:public)
 *   200 -> { "release_features": ["agent.schedules", ...] }
 *   any non-200 -> treated as fail-closed.
 * MUST NOT use /api/v1/features (byok) nor /api/v1/workspaces/{wid}/features
 * (entitlements) (SPEC-FEATURE-TOGGLES-RULE-003, SCENARIO-005).
 *
 * ── CANONICAL FEATURE-KEY REGISTRY (SEMANTIC DEP / R1 drift guard) ──────────
 * `CANONICAL_FEATURE_KEYS` below is the frontend half of a SHARED contract: the
 * backend route-gate (TASK-FT-ROUTE-GATE route->feature map) and the
 * config-as-code source (`deploy/feature-toggles/feature-toggles.yaml`,
 * TASK-FT-CONFIG-DELIVERY) use the SAME canonical dot-key namespace. Keeping the
 * strings identical is what prevents the backend 404 and the frontend hiding
 * from drifting once the legacy `config/featureVisibility.ts` is removed by the
 * Wave-3 per-SPA migration tasks.
 *
 * Keys are DOT-separated canonical (e.g. "agent.schedules", "admin.system") per
 * the HLD-015 capability convention — NOT the legacy colon "app:path" format.
 * The registry is derived 1:1 from the keys in the legacy `featureVisibility.ts`
 * (colon->dot, slash->dot). New gated features add their key here AND in the
 * backend map AND in the config source.
 *
 * @module feature-gate/releaseFeatures
 * @see SPEC-FEATURE-TOGGLES
 */

// ---------------------------------------------------------------------------
// Canonical feature-key registry
// ---------------------------------------------------------------------------

/**
 * The canonical, dot-separated release-toggle keys recognised by the platform.
 * Mirrors the legacy `featureVisibility.ts` namespace (colon->dot, slash->dot):
 *   tenant:sources/website  -> tenant.sources.website
 *   agent:schedules         -> agent.schedules
 *   admin:tenants/presets   -> admin.tenants.presets
 *   global:accessibility    -> global.accessibility
 */
export const CANONICAL_FEATURE_KEYS = [
  // ── Tenant workspace-level ────────────────────────────────────────────────
  "tenant.sources.files",
  "tenant.sources.website",
  "tenant.settings.permissions",
  // Workspace Settings → "Metadata" tab (the Schemas page). Hidden because
  // workspace document upload is feature-flagged off, so per-agent/workspace
  // metadata schemas are not relevant yet. Fail-closed: absent from the enabled
  // set ⇒ hidden.
  "tenant.settings.schemas",
  // Workspace Settings → "Extensions"/Plugins tab. Hidden fail-closed: the
  // tenant-scoped plugin-management surface is unfinished. OFF ⇒ the nav item
  // and its route are both omitted.
  "tenant.settings.plugins",
  // Workspace Settings -> "AI answer records" (SPEC-FRONTEND-UI-058,
  // TASK-AIMARK-TENANT-PROVENANCE-SETTINGS-UI): the tenant's control of its
  // answer-provenance records. OFF by default; released per deployment once the
  // tenant control API (TASK-AIMARK-TENANT-CONTROL-API) is live there.
  "tenant.settings.answer-provenance",
  "tenant.applications",
  // Workspace-members "Invite members" UI (button + bulk-invite email dialog).
  // Hidden by default (fail-closed): there is NO automatic email/SMTP delivery
  // wired — the flow only mints invite links to copy/paste. Flip to `true` only
  // once SMTP delivery exists. Backend invitation endpoints stay live regardless.
  // NOTE: this key also gates the InviteAcceptPage (/invite/:token) — its
  // POST /api/v1/invitations/{id}/accept handler exists but was never mounted
  // on the gateway (docs/research/spread-scan-20260723-120016-fe-dead-routes).
  "tenant.members.invite",
  // Members page "Remove from workspace" control (per-member menu + confirm
  // dialog). Fail-closed OFF — its DELETE /api/v1/workspaces/{id}/members/{uid}
  // has NO backend route (never mounted; PermissionHandler dispatch handles only
  // members-list GET and members/{uid}/role PUT —
  // docs/research/spread-scan-20260723-120016-fe-dead-routes). Flip ON when a
  // member-removal route lands.
  "tenant.members.remove",
  // Chat-history citation markers. Chat history persists no structured citation
  // data, so the baked-in `[N]` markers are inert; gated fail-closed (OFF =>
  // markers stripped at render time in ChatLogs). Display-only, non-destructive.
  "tenant.chatlogs.citations",

  // ── Tenant agent-detail ───────────────────────────────────────────────────
  "agent.ab-testing",
  "agent.schedules",
  // Pinned context files in live chat (verisa_kodelemzes P0-4(b)). Declared ON
  // by the CEO ruling D-KDE-12 (2026-09-28/29, "default on"), and the per-agent
  // "Use pinned files in chat" opt-in (configuration.context_files_in_chat)
  // defaults to on: absent reads as on (utils/contextFilesInChat.ts), only an
  // explicitly saved false is off. While an overlay has the key off (still
  // fail-closed when unresolved), the AI settings page says pinned files are
  // saved but NOT sent to the model in chat, and the opt-in switch is not
  // rendered (SPEC-FRONTEND RULE-063 (2)). Moves with
  // deploy/feature-toggles/feature-toggles.yaml.
  "agent.context-files.chat",
  // Parked no-op agent-settings pages (Notifications / Integrations /
  // Support-channels). Fail-closed OFF: the pages render static placeholders
  // wired to NO backend — routed but broken/no-op (D4, Márton-ruled;
  // STORY-BC-W1-PARKED-SURFACES). Build-vs-delete is the pending W5
  // DECISIONS.md decision (EPIC-FRONTEND-BACKEND-CONTRACT-ALIGNMENT
  // AC-W5-DECISIONS) — parked-pending-decision, not abandoned. Do not flip ON
  // before that ruling.
  "agent.settings.notifications",
  "agent.settings.integrations",
  "agent.settings.support-channels",
  // Orphan DocumentManagerPage (agent-detail `documents` route). Fail-closed
  // OFF: linked from nowhere in web/src (nav-orphan) and its document/graphrag
  // routes are knowingly under-gated pending the fine PEP (D5, Márton-ruled;
  // decision-backed by D-022 2026-07-27). Build-vs-delete is the pending W5
  // DECISIONS.md decision (EPIC AC-W5-DECISIONS; D-022). The former
  // page:document-manager registry slot was removed alongside (no consumer).
  "agent.documents",
  // Merged agent data-sources page (SPEC-FRONTEND-RULE-066, EPIC-AGENT-DATA-SOURCES).
  // ON collapses the Files + Website nav siblings into ONE Sources entry and serves
  // the merged list at `sources`, with the two superseded list paths redirecting to
  // it; OFF leaves today's two entries and two pages untouched. Both arms are live
  // during rollout — TASK-ADS-RETIRE removes the legacy one.
  "agent.sources.unified",
  // The read-only sources tree on the merged Sources page, level 1 of its two-level
  // switch (D-UST-01..03; plan crawled-entities-hierarchy-migration-20261008.md
  // section 11B). OFF renders today's DataSourcesPage unchanged; ON still shows the
  // tree only where the per-town surface agent.sources.index.tree is switched on.
  "agent.sources.tree",
  "agent.sources.website",
  // Crawl-source non-text capture modes (Migration / Archive / Custom on the
  // create form + source detail). FE-only sub-feature of agent.sources.website:
  // when OFF only RAG remains and the Capture Mode selector is not rendered at
  // all (a source already carrying a non-RAG mode stays visible — grandfathered).
  "agent.sources.captureModes",
  // Crawl-source create form: the "Fetch links" PRE-CREATE discovery preview.
  // Fail-closed OFF — the button called the unregistered POST /api/v1/crawl/preview
  // (spec-authz-pdp.routes.yaml:117 records it as no-such-registration → 403), and a
  // proper source-less preview needs the crawler read-leg authz rework
  // (TASK-CF-PREVIEW-API-BE / workspace-scope #2239). Source-scoped preview on the
  // detail page is unaffected. Flip ON once the pre-create preview route is built.
  "agent.sources.website.discovery-preview",
  // Crawl-source DETAIL page: the job-history / retry panel. Fail-closed OFF — the
  // per-job routes (GET /crawl-jobs/{id}, POST /crawl-jobs/{id}/retry, GET
  // /crawl-jobs?source_id=…) have NO backend (removed by the crawler de-merge, never
  // re-specced; route_contract_test.go:62). Hidden until a live-job-state route is
  // built (Wave 2). docs/research/spread-scan-20260723-120016-fe-dead-routes.
  "agent.sources.website.jobs-panel",
  // Crawl-source DETAIL page: the ExcludedPatternsManager panel. Fail-closed OFF —
  // its GET/POST/DELETE /crawl-sources/{id}/excluded-patterns routes have NO backend
  // (dead de-merge routes; excluded_patterns lives in crawl_config, written via the
  // update route). Hidden until folded into the update flow (Wave 2).
  "agent.sources.website.excluded-patterns",
  // Crawl-source DETAIL page: the ExtractionSchemaConfig panel. Fail-closed OFF —
  // its GET/PUT/DELETE /crawl-sources/{id}/extraction-schema routes have NO backend
  // (dead de-merge routes; route_contract_test.go allowedUnmatched — only the legacy
  // agent-scoped /api/agents/{id}/extraction-schema ever existed). The panel's
  // on-mount GET 403s (unmapped_route) 8× per page load. Hidden until an
  // extraction-schema route is built on the crawler-framework surface (Wave 2).
  // P1-d, docs/handoff/2026-07-23-crawler-full-pipeline-handoff.md.
  "agent.sources.website.extraction-schema",
  // Crawl-source DETAIL page: the "Collected Data" CrawlDataTree panel. Fail-closed
  // OFF — its GET /crawl-sources/{id}/documents/tree read has NO backend and NO authz
  // mapping, so it 403s `unmapped_route` under deny-by-default (owner-repro'd on
  // staging 2026-07-29, 4× per page load), and its exclude write
  // (POST /crawl-sources/{id}/exclude) is dead too (route_contract_test.go
  // allowedUnmatched). MISSED by the Wave-1 gating sweep that hid its three sibling
  // panels above: the tree call hoisted its URL into a `const url` variable, which the
  // ROUTE-GATE extractor cannot see (feClientCallRe needs a literal first argument),
  // so the route never appeared in the spread-scan inventory. NOT repointable to the
  // live agent-keyed GET /api/agents/{id}/documents/tree — that route serves the
  // manual FOLDER tree ({folders,documents}, document_dto.go:38), a different
  // capability from this URL-hierarchy tree of crawled pages
  // ({root_path,children[{full_url,is_excluded,type}],total_documents}). Hidden until
  // a crawled-page tree route is built on the crawler-framework surface (Wave 2).
  "agent.sources.website.crawled-data-tree",
  // ── Demo-scope hides (2026-07-29, Márton): generic-crawler config surfaces
  // not needed for the 4-town demo. All FE-only sub-features of
  // agent.sources.website except `delete`, which is also backend-gated.
  // Crawler picker (CrawlerSelector + CrawlerLockControl, detail panel + create
  // form). Towns don't choose a crawler — generic is the default and bespoke
  // crawlers are platform-assigned (SPEC-CRAWLER-FW-RULE-013).
  "agent.sources.website.crawler-picker",
  // Denoising / strip-rule editor (DenoisingRulesEditor, detail panel + create
  // form Advanced Options).
  "agent.sources.website.denoising",
  // Source-detail "Preview & discovery" card (UrlPreview: raw/denoised content
  // preview + discovery URL groups). Distinct from the pre-create
  // discovery-preview key above (different surface, different backend gap).
  "agent.sources.website.preview",
  // Stealth mode surfaces (detail display card + Edit-Configuration dialog
  // stealth/advanced section). Create form keeps sending its default
  // stealth_level silently — hiding is UI-only.
  "agent.sources.website.stealth",
  // Content retention card (archive/cache/none radios) on detail + create
  // Advanced Options. The card's radios are LIVE; the re-clean action inside it
  // has its own key below.
  "agent.sources.website.retention",
  // "Apply to existing pages" (re-clean) INSIDE the retention card. Split out
  // and fail-closed OFF 2026-08-18 (owner ruling on DECISIONS.md D-101): the
  // action dispatches work that can never succeed — nothing enumerates the
  // retained pages (snapshot keys embed a one-way url hash, no object metadata
  // carries the url, the shared S3 client cannot list, and no durable record
  // maps a source's pages to snapshot keys), so every dispatch answers
  // "unavailable". Flip ON when D-101 option A lands (crawler-owned
  // retained_pages table written at snapshot-write time + the persist leg).
  "agent.sources.website.retention.reclean",
  // Attachments toggle (crawl linked PDF/Office) on detail panel + create form.
  "agent.sources.website.attachments",
  // Source delete controls (detail footer Delete / "Stop & Delete" + list-row
  // delete). Fail-closed OFF — same dead-route class as the Wave-1 panels above:
  // the FE's DELETE /crawl-sources/{id} has NO registered backend route (only
  // PUT exists on the crawler-framework pattern; routes.go:1625), because the
  // delete contract (SPEC-CRAWLER-FW API-015) is twice audit-FAILED "DO NOT
  // IMPLEMENT AS-IS" and RULE-026 (in-flight-crawl refusal) is unimplemented.
  // The control can only error or mislead. Flip ON once API-015 is re-ratified
  // and a real delete route exists.
  "agent.sources.website.delete",

  // Widget deploy "Surface effects" style menu (WorkspaceDeploySetup /
  // StyleEffectSelector). FE-only, unfinished polish — default OFF (hidden).
  "tenant.widget.surfaceEffects",

  // Widget customizer "Typography" font-config section (WidgetCustomizer /
  // FontConfigSection). FE-only, unfinished polish — default OFF (hidden).
  "tenant.widget.typography",

  // ── Agent sub-features (restored from [feature-hidden:] tags, Wave-3) ───────
  // The document-metadata feature as a whole: the agent Sources → Schemas page
  // + its sidebar entry, the metadata dialog that opens on EVERY document
  // upload, and the per-document "Edit Metadata" dialog + context-menu row.
  // Also backend-gated (five route patterns in feature_gate.go → 404).
  // Gated fail-closed 2026-08-25 (owner-directed) because the feature does not
  // work end to end, on three independent counts:
  //   1. The document-service upload route accepts only file/folder_id/
  //      replace_of, so the schema_id + metadata_values every FE upload site
  //      sends are SILENTLY DISCARDED (STORY-BC-W1-UPLOAD-METADATA, approved
  //      and unimplemented; boundary-contract ledger row M/docs H-7, UNCOVERED).
  //   2. ARTIS's GenerateTreeJob carries no metadata field at all, so
  //      TreeSkeleton.metadata never holds the six document fields
  //      node_embedder.py stamps into every vector — ARTIS's own comment says
  //      so (plugins/artis/src/search/engine.py:466-468).
  //   3. That Qdrant metadata payload is never read back on the retrieval path,
  //      so the 0.15-weight metadata boost can match nothing.
  // The auto-detector additionally returns every regex/CSV hit with no
  // confidence floor and no cap, so a wide spreadsheet fills the dialog with
  // junk. Flip ON only when TASK-BC-H7-UPLOAD-BE/-FE have landed AND a
  // measurement shows document metadata actually changes retrieval — no such
  // benchmark exists today. Siblings already OFF: `tenant.settings.schemas`
  // (the workspace copy of the Schemas page) and `agent.schema-auto-select`
  // (the auto-pick heuristic inside the dialog); the three are one family.
  "agent.metadata",
  "agent.schema-auto-select",
  "agent.file-preview",
  "agent.folder-management",
  "agent.chat-logs.source-filter",
  "agent.deploy.how-it-works",
  "agent.deploy.swagger-link",
  "agent.deploy.permalink",
  // Conversation Insights tab (tenant agent-detail). Fail-closed, default-OFF
  // until the analysis flow is E2E-verified, then released via the toggle
  // system. WITHOUT this key both the sub-route (routes.tsx) and the sidebar
  // tab (AgentDetailSidebar) are omitted — the whole feature ships invisible.
  // (SPEC-CONVERSATION-INSIGHTS; TASK-CI-FE-INSIGHTS-TAB CHG-07.)
  "agent.insights",
  // Agent training page: the workspace-documents → agent knowledge picker
  // (DocumentSelectionPanel). Unfinished — its add/remove backend is stubbed —
  // and independent of the workspace upload page (tenant.sources.files), so it
  // carries its own fail-closed key rather than riding on that one.
  "agent.training.document-selection",

  // ── Tenant chat playground ────────────────────────────────────────────────
  // System-prompt preset template dropdown (Base / Customer Support /
  // Technical) in ChatSettingsTab. Hidden fail-closed — unfinished polish.
  "tenant.playground.presets",
  // Playground debug monitor toggle (BugReport icon button). Hidden
  // fail-closed — developer-facing diagnostics not meant for tenant admins.
  "tenant.playground.debug",
  // Playground full-screen toggle (OpenInFull / CloseFullscreen). Hidden
  // fail-closed — unfinished polish.
  "tenant.playground.fullscreen",
  // Playground citation/provenance UX: the GroupedSourcesPanel (+ FORRÁSOK tab)
  // AND the inline [N] citation chips in the answer text. Hidden fail-closed
  // (OP #2236): simple RAG's sources[] is everything retrieved, not what the
  // answer used, so the panel over-claims provenance — disabled platform-wide
  // until RAG emits cited-only sources. The animated waiting/shimmer messages
  // are NOT gated by this key. Flip ON when #2236 lands.
  "tenant.playground.citations",

  // ── Parked plugins (backend-only keys — there is no UI to hide) ────────────
  // Both ASR plugins are unreviewed AI-generated leftovers that nobody has
  // tested or seen (owner decision 2026-08-25, DECISIONS.md D-138). Their
  // Deployments are scaled to replicas 0, so these keys exist purely to make
  // the gateway routes answer an honest 404 (SPEC-FEATURE-TOGGLES RULE-004)
  // instead of proxying to a service with no pod and timing out. Unusually for
  // this registry there is NO frontend consumer to gate — a full-tree route
  // inventory found zero references to any of their nine endpoints in web/src,
  // which is precisely why they are parked.
  //
  // audio-transcription: EPIC-AUDIO-TRANSCRIPTION was marked `implemented`
  // while all six of its user-facing acceptance criteria (upload, select model,
  // track status, export, search) were undemonstrable. Corrected to `backlog`.
  "plugin.audio-transcription",
  // realtime-asr: EPIC-REALTIME-ASR AC-003 promises live captions "streamed to
  // connected clients via WebSocket" — there is no WebSocket client in web/src.
  // The backend (incl. the Teams/Zoom meeting bot) is real, hence parked rather
  // than deprecated. Corrected to `backlog`.
  "plugin.realtime-asr",

  // ── Document processing / privacy (declared by TASK-TSURF-W0-RELEASE-KEY-DRIFT) ─
  // OCR for scanned documents - a processing-path gate that is ALSO a frontend
  // gate (D-258). Was declared only in feature-toggles.yaml; registered here so
  // the two lists are equal as sets (EPIC-TENANT-SURFACES-AC-001 (b)).
  "document.ocr",
  // Agent AI settings: the PII anonymizer mode dropdown (AgentSettingsAI). A local
  // stub hardcoded it OFF, shadowing this gate; it now reads the real release set.
  // Fail-closed OFF in feature-toggles.yaml - no environment changes today.
  "privacy.anonymizer",

  // ── Admin ─────────────────────────────────────────────────────────────────
  "admin.tenants.presets",
  // Platform crawler catalogue governance (CrawlerConfigPage: tenant_selectable
  // + default config_overrides per approved crawler) — the admin half of the
  // crawler framework (EPIC-CRAWLER-FRAMEWORK). Fail-closed OFF; released ONLY
  // per environment via VERISA_FEATURE_OVERRIDES (dev/staging ON, prod + gov
  // pinned OFF — product decision 2026-07-14). The tenant half rides
  // "agent.sources.website". Backend routes 404 via feature_gate.go.
  "admin.crawlers",
  // CE/CRA Compliance page (/admin/compliance). A DARK SLICE: Documents, SBOM,
  // Status, Vulnerabilities and Advisories are delivered; AI monitoring and
  // Incidents have no registered backend and render a typed "not yet
  // available" state. Fail-closed OFF in the base config; released per
  // environment via VERISA_FEATURE_OVERRIDES. Releasing it also publishes the
  // delivered compliance document set, which is still marked VAZLAT (draft),
  // and the DoC is a template - so which environments see it is an OWNER
  // decision (queued), not an implementation default.
  "admin.compliance",
  // Per-tenant surface configuration: the "Felületek" tab of the Tenant Detail
  // page and the surface-template pages behind it (SPEC-TENANT-SURFACES
  // UI-001/UI-002). Fail-closed OFF - the feature ships dark. This key is
  // declared in THREE places that must agree: here, in
  // deploy/feature-toggles/feature-toggles.yaml (the parity test proves those
  // two) and in the inline ConfigMap of k8s/base/services/verisa-app.yaml,
  // which is the copy the deployed platform actually serves and which no gate
  // checks (EPIC-TENANT-SURFACES AC-018 - the commit is the evidence).
  "admin.tenants.surfaces",
  // LLM models page (signed catalog, internal engines, switch history) and the
  // relay agents pages (ADR inference-relay-and-local-engines section 10.7,
  // 11.5). Declared per SPEC-FEATURE-TOGGLES-RULE-008 and RELEASED (`true`) in
  // the base config: the platform-admin app is an internal surface (D-347), and
  // the gov cut-over needs both pages. ON EVERYWHERE by CEO ruling 2026-09-29
  // (DECISIONS.md D-RLY-59: "On everywhere"); every overlay's overrides patch
  // pins both true. Declared in the same three places as
  // admin.tenants.surfaces, and gating the backend routes too
  // (internal/api/middleware/feature_gate.go: /api/v1/admin/relay-agents*,
  // /api/v1/admin/llm-engines*). /api/v1/admin/llm-models/catalog stays
  // ungated: the ungated Models page reads it too.
  "admin.llm-models",
  "admin.relay-agents",
  // The cost ledger page (/admin/costs, D-CST-01 A): ON everywhere, as the two
  // above (D-RLY-59); declared in the same three places.
  "admin.costs",
  "admin.knowledge-libraries",
  // Agentic research platform policy page (/admin/agentic-research) and its
  // nav entry (docs/architecture/agentic-research.md; D-AGR-01..04).
  // Fail-closed OFF: the plugin ships dark and is released per environment.
  // Declared in the same places as admin.tenants.surfaces, and gating the
  // plugin's API prefixes in internal/api/middleware/feature_gate.go.
  "admin.agentic-research",
  // Translator (PR #2368, DECISIONS.md D-TRL-01..05): ONE key for every
  // Translator surface - the agent-type create card (AgentsPage), the tool page
  // (plugins/translator/Translator.tsx), the platform-admin price page
  // (/admin/translator) and its nav entry, and the plugin's API prefixes in
  // internal/api/middleware/feature_gate.go. Fail-closed OFF: the plugin ships
  // dark (replicas 0) and is released per environment.
  "plugin.translator",
  "plugin.batch-voice",

  // ── Tenant settings: custom roles ─────────────────────────────────────────
  // RolesPage gated custom roles behind this key under the LEGACY fail-OPEN gate
  // (defaulted visible). Registered here + released `true` in the config so the
  // fail-closed migration preserves its current visibility (no regression).
  "custom_roles.enabled",

  // ── Global (cross-app) ────────────────────────────────────────────────────
  "global.accessibility",
  // Dev-only chat scroll-geometry tuner (ScrollLab overlay in the shared
  // ChatWidget). NOT a user feature — a slider instrument for dialing the scroll
  // pin offset against the live widget. Fail-closed OFF; flip global.scrollLab in
  // feature-toggles.yaml + restart api-gateway to expose it, or use ?scrolllab=1
  // for a quick dev override.
  "global.scrollLab",
] as const;

export type CanonicalFeatureKey = (typeof CANONICAL_FEATURE_KEYS)[number];

const CANONICAL_SET: ReadonlySet<string> = new Set<string>(
  CANONICAL_FEATURE_KEYS,
);

/**
 * Whether `key` is part of the documented canonical registry. Registry
 * membership ONLY — it does NOT mean the key is enabled. Enabled state comes
 * solely from the server-resolved set (`isEnabled`). For dev tooling / tests.
 */
export function isCanonicalFeatureKey(key: string): key is CanonicalFeatureKey {
  return CANONICAL_SET.has(key);
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Base URL for the public release-features endpoint. Mirrors the axios client's
 * BASE_URL resolution (Vite env or origin-relative `/api/v1`) but is fetched
 * with a bare `fetch` so NO Authorization/CSRF interceptor is attached — the
 * endpoint is public and release state must resolve before auth bootstraps.
 */
const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) || "/api/v1";

const RELEASE_FEATURES_PATH = "/release-features";

/** Hard timeout so a hung gateway cannot block first paint indefinitely. */
const BOOTSTRAP_TIMEOUT_MS = 8000;

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/** Lifecycle of the one-shot bootstrap. */
export type BootstrapStatus = "idle" | "loading" | "ready" | "error";

interface ReleaseFeaturesState {
  status: BootstrapStatus;
  /** Resolved set of enabled keys. Empty until a successful bootstrap. */
  enabled: ReadonlySet<string>;
  /** In-flight bootstrap promise so concurrent callers share one fetch. */
  promise: Promise<void> | null;
}

const EMPTY: ReadonlySet<string> = new Set<string>();

const state: ReleaseFeaturesState = {
  status: "idle",
  enabled: EMPTY,
  promise: null,
};

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of listeners) l();
}

/** Subscribe to store changes (used by useFeature's useSyncExternalStore). */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// ---------------------------------------------------------------------------
// Selectors (read-only consumers — NO client-side re-evaluation)
// ---------------------------------------------------------------------------

/** Current resolved enabled set (empty until/unless a successful bootstrap). */
export function getEnabledSet(): ReadonlySet<string> {
  return state.enabled;
}

/** Current bootstrap status. */
export function getStatus(): BootstrapStatus {
  return state.status;
}

/**
 * Resolve a single key against the server set. FAIL CLOSED: returns true ONLY
 * when the key is present in a successfully-resolved set. Unknown keys, and any
 * non-`ready` state (idle/loading/error), return false
 * (SPEC-FEATURE-TOGGLES-RULE-001/006, SCENARIO-001).
 */
export function isEnabled(key: string): boolean {
  if (state.status !== "ready") return false;
  return state.enabled.has(key);
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

function parseReleaseFeatures(body: unknown): ReadonlySet<string> {
  // Fail closed on any shape that is not the documented contract.
  if (body === null || typeof body !== "object") return EMPTY;
  const raw = (body as { release_features?: unknown }).release_features;
  if (!Array.isArray(raw)) return EMPTY;
  const keys = raw.filter((k): k is string => typeof k === "string");
  return new Set<string>(keys);
}

async function doFetch(signal: AbortSignal): Promise<void> {
  const url = `${API_BASE_URL}${RELEASE_FEATURES_PATH}`;
  let resp: Response;
  try {
    resp = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      // Public endpoint: no credentials needed; keep CORS simple.
      signal,
    });
  } catch {
    // Network error / abort / timeout -> fail closed.
    state.status = "error";
    state.enabled = EMPTY;
    return;
  }

  if (!resp.ok) {
    // Any non-200 (incl. the documented 500) -> fail closed.
    state.status = "error";
    state.enabled = EMPTY;
    return;
  }

  let json: unknown;
  try {
    json = await resp.json();
  } catch {
    state.status = "error";
    state.enabled = EMPTY;
    return;
  }

  state.enabled = parseReleaseFeatures(json);
  state.status = "ready";
}

/**
 * Bootstrap the release-features set ONCE. Idempotent: concurrent and repeated
 * calls share a single in-flight fetch and, once resolved, return immediately.
 * Never rejects — failures fail closed into an empty set + `error` status so
 * the SPA can render an operator-visible error WITHOUT enabling anything.
 *
 * SPAs MUST await this before rendering gated routes/nav (first meaningful
 * paint blocks on a resolved-or-failed-closed state).
 */
export function bootstrapReleaseFeatures(): Promise<void> {
  if (state.status === "ready" || state.status === "error") {
    return Promise.resolve();
  }
  if (state.promise) return state.promise;

  state.status = "loading";
  emit();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), BOOTSTRAP_TIMEOUT_MS);

  state.promise = doFetch(controller.signal).finally(() => {
    clearTimeout(timer);
    emit();
  });

  return state.promise;
}

// ---------------------------------------------------------------------------
// Test seam
// ---------------------------------------------------------------------------

/**
 * Reset the store. FOR TESTS ONLY — never call in app code. Lets each unit test
 * exercise a fresh bootstrap (success / 500 / empty / malformed / unknown-key).
 */
export function __resetReleaseFeaturesForTests(): void {
  state.status = "idle";
  state.enabled = EMPTY;
  state.promise = null;
  emit();
}

/**
 * Inject a resolved set directly. FOR TESTS ONLY. Bypasses the network so tests
 * of useFeature / FeatureGate / buildGatedRoutes can pin a known enabled set.
 */
export function __setEnabledForTests(
  keys: Iterable<string>,
  status: BootstrapStatus = "ready",
): void {
  state.enabled = new Set<string>(keys);
  state.status = status;
  state.promise = Promise.resolve();
  emit();
}
